import { getRuntimeConfig } from '../config/runtime';
import { logger } from '../utils/logger';

export interface S3Document {
  key: string;
  fileName: string;
  size: number;
  lastModified: string;
  downloadUrl: string;
}

function getApiUrl(): string {
  const config = getRuntimeConfig();
  return config?.VITE_C1_API_URL ||
    (import.meta.env.VITE_C1_API_URL as string) ||
    'https://ppu7ft7aqe.execute-api.ap-southeast-2.amazonaws.com/dev1';
}

// ── In-memory cache for draft documents (valid for 10 min) ──────────────────
let cachedDrafts: S3Document[] | null = null;
let cacheTimestamp = 0;
const CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes (presigned URLs expire in 15 min)


/**
 * List draft documents via REST endpoint.
 * GET /documents/drafts — no auth required, returns presigned download URLs.
 */
export async function getDraftDocuments(): Promise<S3Document[]> {
  // Return cache if still valid
  if (cachedDrafts && Date.now() - cacheTimestamp < CACHE_TTL_MS) {
    return cachedDrafts;
  }

  try {
    const res = await fetch(`${getApiUrl()}/agreements/documents/drafts`);
    if (!res.ok) {
      logger.warn('[agreementService] GET /documents/drafts failed, falling back to direct S3', { status: res.status });
      return cachedDrafts || getDirectDraftDocuments();
    }
    const data = await res.json();
    const documents: Array<{ fileName: string; downloadUrl: string }> = data.documents || [];

    cachedDrafts = documents.map(d => ({
      key: new URL(d.downloadUrl).pathname.slice(1), // derive S3 key from URL path
      fileName: d.fileName.split('/').pop() || d.fileName,
      size: 0,
      lastModified: '',
      downloadUrl: d.downloadUrl,
    }));
    cacheTimestamp = Date.now();

    logger.info('[agreementService] Loaded draft documents from REST API', { count: cachedDrafts.length });
    return cachedDrafts;
  } catch (err) {
    logger.warn('[agreementService] getDraftDocuments failed, falling back to direct S3', { error: err });
    return cachedDrafts || getDirectDraftDocuments();
  }
}




function getDirectDraftDocuments(): S3Document[] {
  return [];
}


export async function getAustracDocuments(): Promise<S3Document[]> {
  try {
    const res = await fetch(`${getApiUrl()}/agreements/documents/austrac`);
    if (res.ok) {
      const data = await res.json();
      const documents: Array<{ key: string; fileName: string; size: number; lastModified: string; downloadUrl: string }> = data.documents || [];
      return documents
        .filter(d => d.key && !d.key.endsWith('/'))
        .map(d => ({
          key: d.key,
          fileName: d.key.split('/').pop() || d.fileName,
          size: d.size || 0,
          lastModified: d.lastModified || '',
          downloadUrl: d.downloadUrl,
        }));
    }
  } catch (err) {
    logger.warn('[agreementService] getAustracDocuments direct fetch failed, falling back', { error: err });
  }

  // Fallback: filter from draft documents
  const all = await getDraftDocuments();
  return all.filter(d => d.key.startsWith('drafts/austrac/'));
}

/**
 * Get a presigned download URL for a specific draft file.
 * Fetches all drafts from the REST endpoint and returns the matching URL.
 * All documents (including agreement.pdf) are served from S3 via presigned URLs.
 */
export async function getDraftDocumentUrl(fileName: string): Promise<string | null> {
  try {
    const drafts = await getDraftDocuments();
    const doc = drafts.find(d => d.fileName === fileName);
    if (doc?.downloadUrl) {
      logger.info('[agreementService] Found draft URL for', { fileName });
      return doc.downloadUrl;
    }
    logger.warn('[agreementService] Draft not found', { fileName });
    return null;
  } catch (err) {
    logger.error('[agreementService] getDraftDocumentUrl failed', { fileName, error: err });
    return null;
  }
}

/**
 * Upload a signed PDF to S3 via presigned PUT URL.
 *
 * 1. POST /documents/upload-signed { fileName } + session_token header
 *    → returns { uploadUrl } (presigned S3 PUT URL)
 * 2. PUT the PDF bytes to the presigned URL
 *
 * @returns The S3 key of the uploaded file, or null on failure
 */
export async function uploadSignedDocument(
  fileName: string,
  pdfBytes: Uint8Array,
  sessionToken: string
): Promise<string | null> {
  try {
    // Step 1: Get presigned PUT URL from backend
    logger.info('[agreementService] Requesting presigned upload URL', { fileName });
    const res = await fetch(`${getApiUrl()}/documents/upload-signed`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Registration-Session': sessionToken,
      },
      body: JSON.stringify({ fileName }),
    });

    if (!res.ok) {
      const errBody = await res.text();
      logger.error('[agreementService] upload-signed request failed', {
        status: res.status,
        body: errBody,
      });
      return null;
    }

    const data = await res.json();
    const uploadUrl: string = data.uploadUrl;

    if (!uploadUrl) {
      logger.error('[agreementService] No uploadUrl in response', { data });
      return null;
    }

    // Step 2: PUT the PDF bytes to S3 via presigned URL
    logger.info('[agreementService] Uploading signed PDF to S3', { fileName, size: pdfBytes.length });
    const contentType = fileName.toLowerCase().endsWith('.docx')
      ? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      : 'application/pdf';
    const putRes = await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': contentType },
      body: new Uint8Array(pdfBytes) as unknown as BodyInit,
    });

    if (!putRes.ok) {
      logger.error('[agreementService] S3 PUT failed', { status: putRes.status });
      return null;
    }

    // Return the S3 key (derived from the URL path)
    const s3Key = data.s3Key || `temp/${sessionToken}/${fileName}`;
    logger.info('[agreementService] Signed document uploaded successfully', { s3Key });
    return s3Key;
  } catch (err) {
    logger.error('[agreementService] uploadSignedDocument failed', { error: err });
    return null;
  }
}
